import { useCallback, useEffect, useState } from "react";
import {
  listarEventosSeguranca,
  resumoSeguranca,
  linhaDoTempoSeguranca,
  analiseUsuariosEIps,
  alertasSeguranca,
  listarNotificacoesSeguranca,
  criarNotificacaoSeguranca,
  desativarNotificacaoSeguranca,
  diagnosticoSaude,
  diagnosticoCabecalhos,
  diagnosticoExposicao,
  diagnosticoSegredos,
  diagnosticoIsolamento,
} from "../api";
import type {
  EventoSeguranca,
  ResumoSeguranca,
  LinhaDoTempoSeguranca,
  AnaliseUsuariosEIps,
  AlertasSeguranca,
  NotificacaoSeguranca,
  SaudeSistema,
  CabecalhosSistema,
  ExposicaoSistema,
  SegredoSistema,
  IsolamentoSistema,
} from "../api";
import { IconShield, IconActivity, IconLock, IconAlertTriangle, IconUser, IconBell, IconBeaker, IconGrid } from "../icons";
import { SYSTEMS } from "../integration";
import { ordenarPorNome } from "../ordem";

type Secao = "visao-geral" | "alertas" | "notificacoes" | "diagnostico" | "eventos";

type AbaDiagnostico = "saude" | "cabecalhos" | "exposicao" | "segredos" | "isolamento";

const ABAS_DIAGNOSTICO: { id: AbaDiagnostico; nome: string }[] = [
  { id: "saude", nome: "Saúde dos sistemas" },
  { id: "cabecalhos", nome: "Cabeçalhos HTTP" },
  { id: "exposicao", nome: "Exposição de .env" },
  { id: "segredos", nome: "Segredos padrão" },
  { id: "isolamento", nome: "Isolamento do painel" },
];

const TIPOS_AVISO: { id: NotificacaoSeguranca["tipo"]; nome: string }[] = [
  { id: "falha", nome: "Falha / incidente" },
  { id: "manutencao", nome: "Manutenção / atualização" },
  { id: "comunicado", nome: "Comunicado geral" },
];

// "Todos" e "Portal" ficam fixos no topo; os sistemas vêm em ordem alfabética
// (cópia — a ordem do SYSTEMS é a dos cards e não muda).
const ALVOS_AVISO = [
  { id: "", nome: "Todos os sistemas" },
  { id: "portal", nome: "Portal" },
  ...ordenarPorNome(SYSTEMS, (s) => s.nome).map((s) => ({ id: s.id, nome: s.nome })),
];

const ACOES = [
  { id: "", nome: "Todas as ações" },
  { id: "login_sucesso", nome: "Login (sucesso)" },
  { id: "login_falha", nome: "Login (falha)" },
  { id: "create", nome: "Criação" },
  { id: "update", nome: "Edição" },
  { id: "delete", nome: "Exclusão" },
  { id: "download", nome: "Download" },
  { id: "generate", nome: "Geração de documento" },
];

function fmtData(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
}

/** Data + hora curtas — usado nos extremos do eixo do gráfico de 48h, que
 *  cruza 2 dias (só a hora seria ambígua: "17:00" não deixa claro qual dia). */
function fmtHoraComData(iso: string): string {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  } catch {
    return iso;
  }
}

function fmtDiaCurto(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  } catch {
    return iso;
  }
}

const POR_PAGINA = 50;

/**
 * Formata o IP pra exibição: "::1" (loopback IPv6) e o prefixo IPv4-mapeado
 * "::ffff:" viram o IPv4 equivalente, mais reconhecível — "::1" é
 * tecnicamente correto (é o mesmo que 127.0.0.1), mas parece "incompleto"
 * pra quem não é técnico. O valor bruto continua salvo como veio; isto é
 * só apresentação.
 */
function fmtIp(ip: string | null): string {
  if (!ip) return "—";
  if (ip === "::1") return "127.0.0.1 (local)";
  if (ip.startsWith("::ffff:")) return ip.slice(7);
  return ip;
}

/** Arredonda pra um teto "redondo" (1/2/5 × 10^n) — nunca um valor bruto tipo "37". */
function tetoRedondo(valor: number): number {
  if (valor <= 4) return 4;
  const exp = Math.floor(Math.log10(valor));
  const base = valor / 10 ** exp;
  const passo = base <= 1 ? 1 : base <= 2 ? 2 : base <= 5 ? 5 : 10;
  return passo * 10 ** exp;
}

/** Retângulo com cantos arredondados só em cima (a ponta "cresce" da base, que fica reta). */
function barraTopoArredondado(x: number, y: number, w: number, h: number, r: number): string {
  if (h <= 0) return "";
  const raio = Math.max(0, Math.min(r, w / 2, h));
  return `M${x},${y + h} L${x},${y + raio} Q${x},${y} ${x + raio},${y} L${x + w - raio},${y} Q${x + w},${y} ${x + w},${y + raio} L${x + w},${y + h} Z`;
}

/**
 * Gráfico de barras (SVG, sem dependência externa) para a linha do tempo —
 * grade horizontal com valores redondos, barra com ponta arredondada nascendo
 * de uma única base, e tooltip por barra (hover + foco por teclado).
 */
function MiniBarChart({ dados }: { dados: { label: string; total: number; destaque: number }[] }) {
  const [indiceAtivo, setIndiceAtivo] = useState<number | null>(null);

  const maxBruto = Math.max(1, ...dados.map((d) => d.total));
  const max = tetoRedondo(maxBruto);
  const largura = 720;
  const altura = 140;
  const passo = dados.length > 0 ? largura / dados.length : largura;
  const larguraBarra = Math.min(24, Math.max(1, passo - 3));
  const offsetBarra = (passo - larguraBarra) / 2;

  const NUM_LINHAS_GRADE = 4;
  const linhasGrade = Array.from({ length: NUM_LINHAS_GRADE + 1 }, (_, i) => {
    const valor = (max / NUM_LINHAS_GRADE) * i;
    return { valor, y: altura - (valor / max) * altura };
  });

  const ativo = indiceAtivo !== null ? dados[indiceAtivo] : null;

  return (
    <div className="cyberseg-chart-wrap">
      <svg
        viewBox={`0 -8 ${largura + 56} ${altura + 28}`}
        className="cyberseg-chart"
        preserveAspectRatio="none"
        role="img"
        aria-label="Gráfico de eventos ao longo do tempo"
        onMouseLeave={() => setIndiceAtivo(null)}
      >
        {/* Grade + rótulos do eixo Y, sempre valores redondos */}
        {linhasGrade.map((l, i) => (
          <g key={i}>
            <line x1={0} y1={l.y} x2={largura} y2={l.y} className="cyberseg-chart-grid" />
            <text x={largura + 10} y={l.y} dominantBaseline="middle" className="cyberseg-chart-axis-label">
              {Math.round(l.valor).toLocaleString("pt-BR")}
            </text>
          </g>
        ))}

        {dados.map((d, i) => {
          const x = i * passo + offsetBarra;
          const alturaTotal = (d.total / max) * altura;
          const alturaDestaque = (d.destaque / max) * altura;
          const emFoco = i === indiceAtivo;
          return (
            <g key={i}>
              <path
                d={barraTopoArredondado(x, altura - alturaTotal, larguraBarra, alturaTotal, 4)}
                className={emFoco ? "cyberseg-chart-bar cyberseg-chart-bar-ativa" : "cyberseg-chart-bar"}
              />
              {d.destaque > 0 && (
                <path
                  d={barraTopoArredondado(x, altura - alturaDestaque, larguraBarra, alturaDestaque, 4)}
                  className={
                    emFoco ? "cyberseg-chart-bar-destaque cyberseg-chart-bar-ativa" : "cyberseg-chart-bar-destaque"
                  }
                />
              )}
              {/* Alvo de hover/foco maior que a barra em si — cobre a coluna inteira. */}
              <rect
                x={i * passo}
                y={0}
                width={passo}
                height={altura}
                fill="transparent"
                tabIndex={0}
                role="img"
                aria-label={`${d.label}: ${d.total} evento(s), ${d.destaque} falha(s)`}
                onMouseEnter={() => setIndiceAtivo(i)}
                onFocus={() => setIndiceAtivo(i)}
                onBlur={() => setIndiceAtivo(null)}
              />
            </g>
          );
        })}

        <line x1={0} y1={altura} x2={largura} y2={altura} className="cyberseg-chart-baseline" />
      </svg>

      {ativo && (
        <div
          className="cyberseg-chart-tooltip"
          style={{ left: `${((indiceAtivo! + 0.5) / dados.length) * 100}%` }}
        >
          <div className="cyberseg-chart-tooltip-label">{ativo.label}</div>
          <div className="cyberseg-chart-tooltip-row">
            <i className="cyberseg-dot cyberseg-dot-total" />
            <strong>{ativo.total}</strong> <span>evento(s)</span>
          </div>
          <div className="cyberseg-chart-tooltip-row">
            <i className="cyberseg-dot cyberseg-dot-falha" />
            <strong>{ativo.destaque}</strong> <span>falha(s) de login</span>
          </div>
        </div>
      )}

      {dados.length > 0 && (
        <div className="cyberseg-chart-eixo-x">
          <span>{dados[0].label}</span>
          <span>{dados[dados.length - 1].label}</span>
        </div>
      )}
    </div>
  );
}

/** Sparkline mínima (12-24 pontos) para o "trend" de um cartão-resumo. */
function Sparkline({ valores }: { valores: number[] }) {
  if (valores.length < 2) return null;
  const max = Math.max(1, ...valores);
  const largura = 60;
  const altura = 24;
  const passo = largura / (valores.length - 1);
  const pontos = valores.map((v, i) => `${i * passo},${altura - (v / max) * altura}`).join(" ");
  return (
    <svg width={largura} height={altura} viewBox={`0 0 ${largura} ${altura}`} className="cyberseg-sparkline" aria-hidden="true">
      <polyline points={pontos} fill="none" className="cyberseg-sparkline-linha" />
    </svg>
  );
}

interface DeltaInfo {
  valor: number;
  /** Qual direção é a "boa" — omitido/undefined = variação neutra, sem juízo de valor. */
  direcaoBoa?: "queda" | "alta";
}

/** Cartão de estatística: ícone + valor + variação vs. período anterior + tendência. */
function StatCard({
  icone,
  label,
  valor,
  delta,
  trend,
  tom = "normal",
}: {
  icone: React.ReactNode;
  label: string;
  valor: number;
  delta?: DeltaInfo;
  trend?: number[];
  tom?: "normal" | "alerta";
}) {
  let deltaClasse = "cyberseg-card-delta-neutro";
  let deltaTexto = "";
  if (delta && delta.valor !== 0) {
    const subiu = delta.valor > 0;
    deltaTexto = `${subiu ? "+" : ""}${delta.valor}`;
    if (delta.direcaoBoa) {
      const ehBom = delta.direcaoBoa === "alta" ? subiu : !subiu;
      deltaClasse = ehBom ? "cyberseg-card-delta-bom" : "cyberseg-card-delta-ruim";
    }
  } else if (delta) {
    deltaTexto = "0";
  }

  return (
    <div className={tom === "alerta" ? "cyberseg-card cyberseg-card-alerta" : "cyberseg-card"}>
      <div className="cyberseg-card-icone">{icone}</div>
      <div className="cyberseg-card-corpo">
        <div className="cyberseg-card-topo">
          <span className="cyberseg-card-num">{valor.toLocaleString("pt-BR")}</span>
          {delta && (
            <span className={`cyberseg-card-delta ${deltaClasse}`}>
              {deltaTexto} <span className="cyberseg-card-delta-sub">vs. 24h ant.</span>
            </span>
          )}
        </div>
        <span className="cyberseg-card-label">{label}</span>
      </div>
      {trend && trend.length > 1 && (
        <div className="cyberseg-card-trend">
          <Sparkline valores={trend} />
        </div>
      )}
    </div>
  );
}

function AlertaSecao({ titulo, quantidade, children }: { titulo: string; quantidade: number; children: React.ReactNode }) {
  if (quantidade === 0) return null;
  return (
    <div className="cyberseg-alerta-bloco">
      <div className="cyberseg-alerta-titulo">
        ⚠ {titulo} <span className="pill pill-off">{quantidade}</span>
      </div>
      {children}
    </div>
  );
}

/** Selo verde/vermelho de "ok"/"atenção" — usado nos resultados de diagnóstico. */
function SeloOk({ ok, textoOk, textoRuim }: { ok: boolean; textoOk: string; textoRuim: string }) {
  return <span className={ok ? "pill pill-on" : "pill pill-off"}>{ok ? textoOk : textoRuim}</span>;
}

const SECOES: { id: Secao; nome: string; icone: React.ReactNode }[] = [
  { id: "visao-geral", nome: "Visão Geral", icone: <IconActivity size={18} /> },
  { id: "alertas", nome: "Alertas de Segurança", icone: <IconAlertTriangle size={18} /> },
  { id: "notificacoes", nome: "Notificações & Avisos", icone: <IconBell size={18} /> },
  { id: "diagnostico", nome: "Diagnóstico & Testes", icone: <IconBeaker size={18} /> },
  { id: "eventos", nome: "Todos os Eventos", icone: <IconGrid size={18} /> },
];

/**
 * Painel exclusivo do Admin Cybersecurity (admin@cyber.com) — auditoria e
 * segurança de TODOS os 11 sistemas, somente leitura. Nenhuma outra conta,
 * nem o Admin Supremo, enxerga esta tela.
 */
export default function CybersegDashboard() {
  const [secaoAtiva, setSecaoAtiva] = useState<Secao>("visao-geral");

  const [resumo, setResumo] = useState<ResumoSeguranca | null>(null);
  const [timeline, setTimeline] = useState<LinhaDoTempoSeguranca | null>(null);
  const [analiseUsuarios, setAnaliseUsuarios] = useState<AnaliseUsuariosEIps | null>(null);
  const [alertas, setAlertas] = useState<AlertasSeguranca | null>(null);
  const [eventos, setEventos] = useState<EventoSeguranca[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detalheAberto, setDetalheAberto] = useState<EventoSeguranca | null>(null);
  const [grafico, setGrafico] = useState<"48h" | "30d">("48h");

  const [sistema, setSistema] = useState("");
  const [acao, setAcao] = useState("");
  const [usuario, setUsuario] = useState("");

  const [notificacoes, setNotificacoes] = useState<NotificacaoSeguranca[]>([]);
  const [formAviso, setFormAviso] = useState({
    tipo: "comunicado" as NotificacaoSeguranca["tipo"],
    titulo: "",
    mensagem: "",
    sistemaAlvo: "",
    expiraEm: "",
  });
  const [enviandoAviso, setEnviandoAviso] = useState(false);
  const [erroAviso, setErroAviso] = useState<string | null>(null);

  const [abaDiagnostico, setAbaDiagnostico] = useState<AbaDiagnostico>("saude");
  const [rodandoDiagnostico, setRodandoDiagnostico] = useState(false);
  const [erroDiagnostico, setErroDiagnostico] = useState<string | null>(null);
  const [resultadoSaude, setResultadoSaude] = useState<SaudeSistema[] | null>(null);
  const [resultadoCabecalhos, setResultadoCabecalhos] = useState<CabecalhosSistema[] | null>(null);
  const [resultadoExposicao, setResultadoExposicao] = useState<ExposicaoSistema[] | null>(null);
  const [resultadoSegredos, setResultadoSegredos] = useState<SegredoSistema[] | null>(null);
  const [resultadoIsolamento, setResultadoIsolamento] = useState<IsolamentoSistema[] | null>(null);

  async function rodarDiagnostico() {
    setRodandoDiagnostico(true);
    setErroDiagnostico(null);
    try {
      if (abaDiagnostico === "saude") setResultadoSaude((await diagnosticoSaude()).resultados);
      else if (abaDiagnostico === "cabecalhos") setResultadoCabecalhos((await diagnosticoCabecalhos()).resultados);
      else if (abaDiagnostico === "exposicao") setResultadoExposicao((await diagnosticoExposicao()).resultados);
      else if (abaDiagnostico === "segredos") setResultadoSegredos((await diagnosticoSegredos()).resultados);
      else if (abaDiagnostico === "isolamento") setResultadoIsolamento((await diagnosticoIsolamento()).resultados);
    } catch (err) {
      setErroDiagnostico(err instanceof Error ? err.message : "Erro ao rodar a verificação.");
    } finally {
      setRodandoDiagnostico(false);
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, e, t, a, al, n] = await Promise.all([
        resumoSeguranca(),
        listarEventosSeguranca({ sistema, acao, usuario, pagina, porPagina: POR_PAGINA }),
        linhaDoTempoSeguranca(),
        analiseUsuariosEIps(),
        alertasSeguranca(),
        listarNotificacoesSeguranca(),
      ]);
      setResumo(r);
      setEventos(e.eventos);
      setTotal(e.total);
      setTimeline(t);
      setAnaliseUsuarios(a);
      setAlertas(al);
      setNotificacoes(n);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao carregar auditoria.");
    } finally {
      setLoading(false);
    }
  }, [sistema, acao, usuario, pagina]);

  useEffect(() => {
    load();
  }, [load]);

  function aplicarFiltros(e: React.FormEvent) {
    e.preventDefault();
    setPagina(1);
    load();
  }

  async function enviarAviso(e: React.FormEvent) {
    e.preventDefault();
    setEnviandoAviso(true);
    setErroAviso(null);
    try {
      await criarNotificacaoSeguranca({
        tipo: formAviso.tipo,
        titulo: formAviso.titulo,
        mensagem: formAviso.mensagem,
        sistemaAlvo: formAviso.sistemaAlvo || null,
        expiraEm: formAviso.expiraEm ? new Date(formAviso.expiraEm).toISOString() : null,
      });
      setFormAviso({ tipo: "comunicado", titulo: "", mensagem: "", sistemaAlvo: "", expiraEm: "" });
      setNotificacoes(await listarNotificacoesSeguranca());
    } catch (err) {
      setErroAviso(err instanceof Error ? err.message : "Erro ao enviar aviso.");
    } finally {
      setEnviandoAviso(false);
    }
  }

  async function desativarAviso(id: number) {
    try {
      await desativarNotificacaoSeguranca(id);
      setNotificacoes(await listarNotificacoesSeguranca());
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao desativar aviso.");
    }
  }

  const totalPaginas = Math.max(1, Math.ceil(total / POR_PAGINA));

  const totalAlertas = alertas
    ? alertas.forcaBruta.length +
      alertas.horarioIncomum.length +
      alertas.exclusoesEmMassa.length +
      alertas.ipNovo.length
    : 0;

  // Deriva tendência (24 pontos/hora) e variação vs. as 24h anteriores a partir
  // da própria linha do tempo já carregada — sem pedir nada novo ao backend.
  const porHora = timeline?.porHora ?? [];
  const janelaRecente = porHora.slice(-24);
  const janelaAnterior = porHora.slice(0, Math.max(0, porHora.length - 24));
  const somar = (arr: typeof porHora, campo: "total" | "logins" | "falhas") =>
    arr.reduce((acc, h) => acc + h[campo], 0);
  const deltaEventos: DeltaInfo = {
    valor: somar(janelaRecente, "total") - somar(janelaAnterior, "total"),
  };
  const deltaLogins: DeltaInfo = {
    valor: somar(janelaRecente, "logins") - somar(janelaAnterior, "logins"),
  };
  const deltaFalhas: DeltaInfo = {
    valor: somar(janelaRecente, "falhas") - somar(janelaAnterior, "falhas"),
    direcaoBoa: "queda", // subir falhas de login é ruim
  };
  const trendEventos = janelaRecente.map((h) => h.total);
  const trendLogins = janelaRecente.map((h) => h.logins);
  const trendFalhas = janelaRecente.map((h) => h.falhas);

  return (
    <div className="usuarios cyberseg">
      <div className="content-head usuarios-head">
        <div>
          <h1>
            <IconShield size={22} /> Auditoria &amp; Segurança
          </h1>
          <p className="hello">
            Painel exclusivo do Admin Cybersecurity — todas as ações de todos os
            sistemas da suíte SERCAMP, somente leitura.
          </p>
        </div>
      </div>

      {error && <div className="err">{error}</div>}

      <div className="cyberseg-layout">
        <aside className="cyberseg-sidebar">
          <nav className="cyberseg-nav">
            {SECOES.map((s) => (
              <button
                key={s.id}
                type="button"
                className={s.id === secaoAtiva ? "cyberseg-nav-item ativa" : "cyberseg-nav-item"}
                onClick={() => setSecaoAtiva(s.id)}
              >
                <span className="cyberseg-nav-icon">{s.icone}</span>
                {s.nome}
                {s.id === "alertas" && totalAlertas > 0 && (
                  <span className="cyberseg-nav-badge">{totalAlertas}</span>
                )}
              </button>
            ))}
          </nav>
        </aside>

        <div className="cyberseg-conteudo">
          {/* ======================== VISÃO GERAL ======================== */}
          {secaoAtiva === "visao-geral" && (
            <>
              {resumo && (
                <div className="cyberseg-cards">
                  <StatCard
                    icone={<IconActivity size={20} />}
                    label="Eventos registrados"
                    valor={resumo.cards.total_eventos}
                    delta={porHora.length > 0 ? deltaEventos : undefined}
                    trend={trendEventos}
                  />
                  <StatCard
                    icone={<IconLock size={20} />}
                    label="Logins (24h)"
                    valor={resumo.cards.logins_24h}
                    delta={porHora.length > 0 ? deltaLogins : undefined}
                    trend={trendLogins}
                  />
                  <StatCard
                    icone={<IconAlertTriangle size={20} />}
                    label="Falhas de login (24h)"
                    valor={resumo.cards.falhas_login_24h}
                    delta={porHora.length > 0 ? deltaFalhas : undefined}
                    trend={trendFalhas}
                    tom="alerta"
                  />
                  <StatCard
                    icone={<IconUser size={20} />}
                    label="Usuários ativos (24h)"
                    valor={resumo.cards.usuarios_ativos_24h}
                  />
                  <StatCard
                    icone={<IconShield size={20} />}
                    label="Alertas de segurança"
                    valor={totalAlertas}
                    tom={totalAlertas > 0 ? "alerta" : "normal"}
                  />
                </div>
              )}

              {timeline && (
                <div className="cyberseg-secao">
                  <div className="cyberseg-secao-head">
                    <h2>Linha do tempo</h2>
                    <div className="cyberseg-toggle">
                      <button
                        type="button"
                        className={grafico === "48h" ? "btn btn-ghost ativo" : "btn btn-ghost"}
                        onClick={() => setGrafico("48h")}
                      >
                        Últimas 48h
                      </button>
                      <button
                        type="button"
                        className={grafico === "30d" ? "btn btn-ghost ativo" : "btn btn-ghost"}
                        onClick={() => setGrafico("30d")}
                      >
                        Últimos 30 dias
                      </button>
                    </div>
                  </div>
                  {grafico === "48h" ? (
                    <MiniBarChart
                      dados={timeline.porHora.map((h) => ({ label: fmtHoraComData(h.hora), total: h.total, destaque: h.falhas }))}
                    />
                  ) : (
                    <MiniBarChart
                      dados={timeline.porDia.map((d) => ({ label: fmtDiaCurto(d.dia), total: d.total, destaque: d.falhas }))}
                    />
                  )}
                  <div className="cyberseg-chart-legenda">
                    <span><i className="cyberseg-dot cyberseg-dot-total" /> Total de eventos</span>
                    <span><i className="cyberseg-dot cyberseg-dot-falha" /> Falhas de login</span>
                  </div>
                </div>
              )}

              <div className="cyberseg-grid-2">
                {resumo && resumo.porSistema.length > 0 && (
                  <div className="cyberseg-secao">
                    <h2>Por sistema</h2>
                    <div className="usuarios-table-wrap">
                      <table className="usuarios-table">
                        <thead>
                          <tr>
                            <th>Sistema</th>
                            <th>Eventos</th>
                            <th>Último evento</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resumo.porSistema.map((s) => (
                            <tr key={s.sistema}>
                              <td>{s.sistema}</td>
                              <td>{s.total}</td>
                              <td>{fmtData(s.ultimo_evento_em)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {analiseUsuarios && analiseUsuarios.topUsuarios.length > 0 && (
                  <div className="cyberseg-secao">
                    <h2>Usuários mais ativos</h2>
                    <div className="usuarios-table-wrap">
                      <table className="usuarios-table">
                        <thead>
                          <tr>
                            <th>Usuário</th>
                            <th>Eventos</th>
                            <th>IPs distintos</th>
                            <th>Sistemas</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analiseUsuarios.topUsuarios.map((u) => (
                            <tr key={u.usuario}>
                              <td>{u.usuario}</td>
                              <td>{u.total}</td>
                              <td>{u.ips_distintos}</td>
                              <td>{u.sistemas_distintos}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {analiseUsuarios && analiseUsuarios.topIps.length > 0 && (
                  <div className="cyberseg-secao">
                    <h2>IPs mais ativos</h2>
                    <div className="usuarios-table-wrap">
                      <table className="usuarios-table">
                        <thead>
                          <tr>
                            <th>IP</th>
                            <th>Eventos</th>
                            <th>Usuários distintos</th>
                            <th>Último evento</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analiseUsuarios.topIps.map((i) => (
                            <tr key={i.ip}>
                              <td>{fmtIp(i.ip)}</td>
                              <td>{i.total}</td>
                              <td>{i.usuarios_distintos}</td>
                              <td>{fmtData(i.ultimo_evento_em)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ======================== ALERTAS DE SEGURANÇA ======================== */}
          {secaoAtiva === "alertas" && (
            <div className="cyberseg-secao">
              <h2>Alertas de comportamento suspeito</h2>
              {totalAlertas === 0 ? (
                <div className="usuarios-empty">Nenhum alerta no momento.</div>
              ) : (
                <div className="cyberseg-alertas">
                  <AlertaSecao titulo="Possível força bruta (≥3 falhas de login em 15 min)" quantidade={alertas?.forcaBruta.length ?? 0}>
                    <div className="usuarios-table-wrap">
                      <table className="usuarios-table">
                        <thead>
                          <tr>
                            <th>Sistema</th>
                            <th>Usuário</th>
                            <th>Falhas</th>
                            <th>Última falha</th>
                          </tr>
                        </thead>
                        <tbody>
                          {alertas?.forcaBruta.map((f, i) => (
                            <tr key={i}>
                              <td>{f.sistema}</td>
                              <td>{f.usuario}</td>
                              <td>{f.falhas}</td>
                              <td>{fmtData(f.ultima_falha)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </AlertaSecao>

                  <AlertaSecao titulo="Login fora do horário comercial (antes das 6h ou depois das 22h)" quantidade={alertas?.horarioIncomum.length ?? 0}>
                    <div className="usuarios-table-wrap">
                      <table className="usuarios-table">
                        <thead>
                          <tr>
                            <th>Quando</th>
                            <th>Sistema</th>
                            <th>Usuário</th>
                            <th>IP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {alertas?.horarioIncomum.map((h) => (
                            <tr key={h.id}>
                              <td>{fmtData(h.criado_em)}</td>
                              <td>{h.sistema}</td>
                              <td>{h.usuario ?? "—"}</td>
                              <td>{fmtIp(h.ip)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </AlertaSecao>

                  <AlertaSecao titulo="Exclusões em massa (≥5 na mesma hora)" quantidade={alertas?.exclusoesEmMassa.length ?? 0}>
                    <div className="usuarios-table-wrap">
                      <table className="usuarios-table">
                        <thead>
                          <tr>
                            <th>Sistema</th>
                            <th>Usuário</th>
                            <th>Hora</th>
                            <th>Exclusões</th>
                          </tr>
                        </thead>
                        <tbody>
                          {alertas?.exclusoesEmMassa.map((m, i) => (
                            <tr key={i}>
                              <td>{m.sistema}</td>
                              <td>{m.usuario}</td>
                              <td>{fmtData(m.hora)}</td>
                              <td>{m.exclusoes}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </AlertaSecao>

                  <AlertaSecao titulo="IP novo para o usuário (últimas 24h)" quantidade={alertas?.ipNovo.length ?? 0}>
                    <div className="usuarios-table-wrap">
                      <table className="usuarios-table">
                        <thead>
                          <tr>
                            <th>Usuário</th>
                            <th>IP novo</th>
                            <th>Primeira vez visto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {alertas?.ipNovo.map((n, i) => (
                            <tr key={i}>
                              <td>{n.usuario}</td>
                              <td>{fmtIp(n.ip)}</td>
                              <td>{fmtData(n.primeira_vez)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </AlertaSecao>
                </div>
              )}
            </div>
          )}

          {/* ======================== NOTIFICAÇÕES & AVISOS ======================== */}
          {secaoAtiva === "notificacoes" && (
            <div className="cyberseg-secao cyberseg-avisos">
              <h2>Notificações &amp; Avisos</h2>
              <p className="hello cyberseg-avisos-sub">
                Envie um aviso de falha, manutenção ou comunicado geral para um sistema
                específico ou para todos — aparece como banner dentro do próprio sistema.
                Esta é a única ação de escrita que esta conta pode fazer em toda a suíte.
              </p>

              <form className="cyberseg-aviso-form" onSubmit={enviarAviso}>
                {erroAviso && <div className="err">{erroAviso}</div>}
                <div className="cyberseg-aviso-form-row">
                  <label className="field">
                    <span>Tipo</span>
                    <select
                      value={formAviso.tipo}
                      onChange={(e) => setFormAviso((p) => ({ ...p, tipo: e.target.value as NotificacaoSeguranca["tipo"] }))}
                    >
                      {TIPOS_AVISO.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.nome}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span>Sistema alvo</span>
                    <select
                      value={formAviso.sistemaAlvo}
                      onChange={(e) => setFormAviso((p) => ({ ...p, sistemaAlvo: e.target.value }))}
                    >
                      {ALVOS_AVISO.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.nome}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span>Expira em (opcional)</span>
                    <input
                      type="datetime-local"
                      value={formAviso.expiraEm}
                      onChange={(e) => setFormAviso((p) => ({ ...p, expiraEm: e.target.value }))}
                    />
                  </label>
                </div>
                <label className="field">
                  <span>Título</span>
                  <input
                    value={formAviso.titulo}
                    onChange={(e) => setFormAviso((p) => ({ ...p, titulo: e.target.value }))}
                    placeholder="Ex.: Manutenção programada"
                    required
                  />
                </label>
                <label className="field">
                  <span>Mensagem</span>
                  <textarea
                    value={formAviso.mensagem}
                    onChange={(e) => setFormAviso((p) => ({ ...p, mensagem: e.target.value }))}
                    placeholder="Ex.: O sistema ficará indisponível das 22h às 23h para atualização."
                    rows={3}
                    required
                  />
                </label>
                <div className="modal-foot">
                  <button type="submit" className="btn" disabled={enviandoAviso}>
                    {enviandoAviso ? "Enviando..." : "Enviar aviso"}
                  </button>
                </div>
              </form>

              {notificacoes.length > 0 && (
                <div className="usuarios-table-wrap cyberseg-avisos-lista">
                  <table className="usuarios-table">
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Título</th>
                        <th>Alvo</th>
                        <th>Criado em</th>
                        <th>Expira em</th>
                        <th>Situação</th>
                        <th className="col-actions">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notificacoes.map((n) => (
                        <tr key={n.id}>
                          <td>
                            <span className={`pill cyberseg-pill-${n.tipo}`}>
                              {TIPOS_AVISO.find((t) => t.id === n.tipo)?.nome ?? n.tipo}
                            </span>
                          </td>
                          <td>{n.titulo}</td>
                          <td>{n.sistema_alvo ? (ALVOS_AVISO.find((a) => a.id === n.sistema_alvo)?.nome ?? n.sistema_alvo) : "Todos"}</td>
                          <td>{fmtData(n.criado_em)}</td>
                          <td>{n.expira_em ? fmtData(n.expira_em) : "—"}</td>
                          <td>
                            <span className={n.ativo ? "pill pill-on" : "pill pill-off"}>
                              {n.ativo ? "Ativo" : "Desativado"}
                            </span>
                          </td>
                          <td className="col-actions">
                            {n.ativo && (
                              <button className="link-btn link-danger" onClick={() => desativarAviso(n.id)}>
                                Desativar
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ======================== DIAGNÓSTICO & TESTES ======================== */}
          {secaoAtiva === "diagnostico" && (
            <div className="cyberseg-secao cyberseg-diagnostico">
              <h2>Diagnóstico &amp; Testes de Segurança</h2>
              <p className="hello cyberseg-avisos-sub">
                Verificações somente leitura, disparadas manualmente — nenhuma altera
                dado nenhum ou afeta o funcionamento normal dos sistemas.
              </p>

              <div className="cyberseg-diag-abas">
                {ABAS_DIAGNOSTICO.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className={a.id === abaDiagnostico ? "cyberseg-diag-aba ativa" : "cyberseg-diag-aba"}
                    onClick={() => setAbaDiagnostico(a.id)}
                  >
                    {a.nome}
                  </button>
                ))}
              </div>

              <div className="cyberseg-diag-corpo">
                {erroDiagnostico && <div className="err">{erroDiagnostico}</div>}

                <button type="button" className="btn cyberseg-diag-rodar" onClick={rodarDiagnostico} disabled={rodandoDiagnostico}>
                  {rodandoDiagnostico ? "Verificando..." : "Rodar verificação"}
                </button>

                {abaDiagnostico === "saude" && (
                  <>
                    <p className="cyberseg-diag-explicacao">Faz um GET simples em cada sistema e mede o tempo de resposta — não muda nada.</p>
                    {resultadoSaude && (
                      <div className="usuarios-table-wrap">
                        <table className="usuarios-table">
                          <thead>
                            <tr>
                              <th>Sistema</th>
                              <th>Porta</th>
                              <th>Situação</th>
                              <th>Tempo de resposta</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resultadoSaude.map((r) => (
                              <tr key={r.sistema}>
                                <td>{r.nome}</td>
                                <td>{r.porta}</td>
                                <td><SeloOk ok={r.online} textoOk="Online" textoRuim="Fora do ar" /></td>
                                <td>{r.online ? `${r.tempoMs} ms` : "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {abaDiagnostico === "cabecalhos" && (
                  <>
                    <p className="cyberseg-diag-explicacao">Confere se cada sistema envia cabeçalhos HTTP de segurança básicos (CSP, X-Frame-Options etc.) na resposta — só leitura.</p>
                    {resultadoCabecalhos && (
                      <div className="usuarios-table-wrap">
                        <table className="usuarios-table">
                          <thead>
                            <tr>
                              <th>Sistema</th>
                              <th>Situação</th>
                              <th>Presentes</th>
                              <th>Faltando</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resultadoCabecalhos.map((r) => (
                              <tr key={r.sistema}>
                                <td>{r.nome}</td>
                                <td><SeloOk ok={r.online && r.faltando.length === 0} textoOk="Completo" textoRuim={r.online ? "Incompleto" : "Fora do ar"} /></td>
                                <td>{r.presentes.length ? r.presentes.join(", ") : "—"}</td>
                                <td>{r.faltando.length ? r.faltando.join(", ") : "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {abaDiagnostico === "exposicao" && (
                  <>
                    <p className="cyberseg-diag-explicacao">Tenta baixar "/.env" de cada sistema pela URL pública e confere se o CONTEÚDO parece mesmo um .env (não só o status HTTP) — só leitura.</p>
                    {resultadoExposicao && (
                      <div className="usuarios-table-wrap">
                        <table className="usuarios-table">
                          <thead>
                            <tr>
                              <th>Sistema</th>
                              <th>Status HTTP</th>
                              <th>Situação</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resultadoExposicao.map((r) => (
                              <tr key={r.sistema}>
                                <td>{r.nome}</td>
                                <td>{r.status ?? "—"}</td>
                                <td><SeloOk ok={!r.exposto} textoOk="Protegido" textoRuim="EXPOSTO" /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {abaDiagnostico === "segredos" && (
                  <>
                    <p className="cyberseg-diag-explicacao">Lê (do próprio disco, sem rede) se o SECURITY_AUDIT_TOKEN de cada sistema ainda está no valor de exemplo — nunca mostra o valor em si.</p>
                    {resultadoSegredos && (
                      <div className="usuarios-table-wrap">
                        <table className="usuarios-table">
                          <thead>
                            <tr>
                              <th>Sistema</th>
                              <th>Situação</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resultadoSegredos.map((r) => (
                              <tr key={r.sistema}>
                                <td>{r.nome}</td>
                                <td>
                                  {r.status === "customizado" ? (
                                    <span className="pill pill-on">Customizado</span>
                                  ) : r.status === "padrao" ? (
                                    <span className="pill pill-off">Valor padrão — trocar antes de produção</span>
                                  ) : (
                                    <span className="pill pill-off">Ausente</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {abaDiagnostico === "isolamento" && (
                  <>
                    <p className="cyberseg-diag-explicacao">Reconfirma, sob demanda, que o Admin Cybersecurity continua sem nenhum acesso liberado nos 10 sistemas irmãos.</p>
                    {resultadoIsolamento && (
                      <div className="usuarios-table-wrap">
                        <table className="usuarios-table">
                          <thead>
                            <tr>
                              <th>Sistema</th>
                              <th>Situação</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resultadoIsolamento.map((r) => (
                              <tr key={r.sistema}>
                                <td>{r.nome}</td>
                                <td><SeloOk ok={r.isolado} textoOk="Isolado (sem acesso)" textoRuim="ALERTA: tem acesso!" /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* ======================== TODOS OS EVENTOS ======================== */}
          {secaoAtiva === "eventos" && (
            <div className="cyberseg-secao">
              <h2>Todos os eventos</h2>
              <form className="cyberseg-filtros" onSubmit={aplicarFiltros}>
                <input
                  placeholder="Sistema (ex.: rh, laboratorio...)"
                  value={sistema}
                  onChange={(e) => setSistema(e.target.value)}
                />
                <select value={acao} onChange={(e) => setAcao(e.target.value)}>
                  {ACOES.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.nome}
                    </option>
                  ))}
                </select>
                <input
                  placeholder="Usuário/login"
                  value={usuario}
                  onChange={(e) => setUsuario(e.target.value)}
                />
                <button type="submit" className="btn">
                  Filtrar
                </button>
              </form>

              {loading ? (
                <div className="usuarios-empty">Carregando...</div>
              ) : eventos.length === 0 ? (
                <div className="usuarios-empty">Nenhum evento encontrado.</div>
              ) : (
                <>
                  <div className="usuarios-table-wrap">
                    <table className="usuarios-table">
                      <thead>
                        <tr>
                          <th>Quando</th>
                          <th>Sistema</th>
                          <th>Usuário</th>
                          <th>Ação</th>
                          <th>Recurso</th>
                          <th>IP</th>
                          <th className="col-actions">Detalhes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eventos.map((ev) => (
                          <tr key={ev.id}>
                            <td>{fmtData(ev.criado_em)}</td>
                            <td>{ev.sistema}</td>
                            <td>{ev.usuario ?? "—"}</td>
                            <td>
                              <span
                                className={
                                  ev.acao === "login_falha" ? "pill pill-off" : "pill pill-on"
                                }
                              >
                                {ev.acao}
                              </span>
                            </td>
                            <td>
                              {ev.recurso_tipo
                                ? `${ev.recurso_tipo}${ev.recurso_id ? ` #${ev.recurso_id}` : ""}`
                                : "—"}
                            </td>
                            <td>{fmtIp(ev.ip)}</td>
                            <td className="col-actions">
                              <button className="link-btn" onClick={() => setDetalheAberto(ev)}>
                                Ver
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="cyberseg-paginacao">
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={pagina <= 1}
                      onClick={() => setPagina((p) => Math.max(1, p - 1))}
                    >
                      ← Anterior
                    </button>
                    <span>
                      Página {pagina} de {totalPaginas} ({total} evento{total === 1 ? "" : "s"})
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={pagina >= totalPaginas}
                      onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                    >
                      Próxima →
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {detalheAberto && (
        <div className="modal-overlay" onMouseDown={() => setDetalheAberto(null)}>
          <div className="modal-card cyberseg-modal-wide" onMouseDown={(e) => e.stopPropagation()}>
            <h3>Evento #{detalheAberto.id}</h3>
            <div className="cyberseg-detalhe-grid">
              <span>Quando</span>
              <span>{fmtData(detalheAberto.criado_em)}</span>
              <span>Sistema</span>
              <span>{detalheAberto.sistema}</span>
              <span>Usuário</span>
              <span>{detalheAberto.usuario ?? "—"}</span>
              <span>Ação</span>
              <span>{detalheAberto.acao}</span>
              <span>Recurso</span>
              <span>
                {detalheAberto.recurso_tipo
                  ? `${detalheAberto.recurso_tipo}${detalheAberto.recurso_id ? ` #${detalheAberto.recurso_id}` : ""}`
                  : "—"}
              </span>
              <span>IP</span>
              <span>{fmtIp(detalheAberto.ip)}</span>
            </div>
            <span className="cyberseg-detalhe-json-label">Detalhes brutos</span>
            <pre className="cyberseg-detalhe-json">
              {detalheAberto.detalhes ? JSON.stringify(detalheAberto.detalhes, null, 2) : "(sem detalhes adicionais)"}
            </pre>
            <div className="modal-foot">
              <button type="button" className="btn" onClick={() => setDetalheAberto(null)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

